import { settings as settingsApi } from '../api';
import { defaultHomeContent, mergeHomeContent } from './homeContent';

const HOME_CONTENT_CACHE_KEY = 'home_content:public_cache';
const HOME_CONTENT_VERSION_KEY = 'home_content:public_version';
let homeContentPromise = null;
let memoryHomeContent = null;

function getCacheVersion() {
  if (typeof window === 'undefined') return '0';
  try {
    return localStorage.getItem(HOME_CONTENT_VERSION_KEY) || '0';
  } catch {
    return '0';
  }
}

function readCachedHomeContent() {
  if (memoryHomeContent) return memoryHomeContent;
  if (typeof window === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(HOME_CONTENT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== getCacheVersion()) return null;
    memoryHomeContent = mergeHomeContent(parsed.content);
    return memoryHomeContent;
  } catch {
    return null;
  }
}

function readEmbeddedHomeContent() {
  if (typeof document === 'undefined') return null;
  try {
    const raw = document.getElementById('home-content-snapshot')?.textContent;
    return raw ? mergeHomeContent(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCachedHomeContent(value) {
  memoryHomeContent = value;
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(HOME_CONTENT_CACHE_KEY, JSON.stringify({ version: getCacheVersion(), content: value }));
  } catch {
    // ignore cache write failures
  }
}

export function getCachedHomeContentSync() {
  return readEmbeddedHomeContent() || readCachedHomeContent() || defaultHomeContent;
}

export function hasInitialHomeContent() {
  return Boolean(readEmbeddedHomeContent() || readCachedHomeContent());
}

export async function loadHomeContent() {
  const cached = readCachedHomeContent();
  if (cached && !readEmbeddedHomeContent()) return cached;

  if (!homeContentPromise) {
    homeContentPromise = settingsApi.get('home_content')
      .then((res) => {
        const merged = mergeHomeContent(res.value || {});
        writeCachedHomeContent(merged);
        return merged;
      })
      .catch(() => readEmbeddedHomeContent() || defaultHomeContent)
      .finally(() => {
        homeContentPromise = null;
      });
  }

  return homeContentPromise;
}

export function invalidateHomeContentCache() {
  memoryHomeContent = null;
  homeContentPromise = null;
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(HOME_CONTENT_CACHE_KEY);
  } catch {
    // ignore cache cleanup failures
  }
}

export function bumpHomeContentVersion() {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(HOME_CONTENT_VERSION_KEY, String(Date.now()));
  } catch {
    // ignore version write failures
  }
  invalidateHomeContentCache();
  try {
    window.dispatchEvent(new CustomEvent('home_content:updated'));
  } catch {
    // ignore event dispatch failures
  }
}
